import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Home,
    UserCircle,
    TrendingUp,
    LayoutTemplate,
    FileText,
    Mail,
    Puzzle,
    Settings,
    Shield,
    Scale,
    Users,
    Clock,
    Bell,
    Eye,
    Loader2,
} from "lucide-react";
import {
    adminGetFeatureFlags,
    adminUpdateFeatureFlags,
} from "../../lib/api";
import { resetFeatureFlagsCache } from "../../lib/feature_flags";

/**
 * iter-114: Admin → Display tab.
 *
 * Superadmin picks which nav elements are visible across the app.
 * State lives in the same `feature_flags` sub-doc on
 * `app_settings._id="global"` so it persists across container
 * rebuilds (the Mongo volume survives docker compose down/up, and
 * the deploy script preserves `production.env`). All flags default
 * TRUE — a fresh install shows everything and the operator toggles
 * off what they don't want.
 */

const GROUPS = [
    {
        title: "Bottom bar buttons",
        subtitle:
            "The floating pill of buttons on the bottom of every non-workspace page.",
        icon: LayoutTemplate,
        rows: [
            { key: "show_bottom_projects", label: "Projects", icon: Home },
            { key: "show_bottom_contacts", label: "Contacts", icon: UserCircle },
            { key: "show_bottom_leads", label: "Leads", icon: TrendingUp },
            { key: "show_bottom_templates", label: "Templates", icon: LayoutTemplate },
            { key: "show_bottom_email", label: "Email", icon: Mail },
            { key: "show_bottom_plugins", label: "Plugins", icon: Puzzle },
        ],
    },
    {
        title: "Top menu items",
        subtitle:
            "The horizontal navigation across the top of every page (mobile shows these in the hamburger sheet).",
        icon: FileText,
        rows: [
            { key: "show_top_projects", label: "Projects", icon: Home },
            { key: "show_top_contacts", label: "Contacts", icon: UserCircle },
            { key: "show_top_leads", label: "Leads", icon: TrendingUp },
            { key: "show_top_templates", label: "Templates", icon: LayoutTemplate },
            { key: "show_top_reports", label: "Reports", icon: FileText },
            { key: "show_top_email", label: "Email", icon: Mail },
            { key: "show_top_plugins", label: "Plugins", icon: Puzzle },
            { key: "show_top_settings", label: "Settings", icon: Settings },
            { key: "show_top_privacy", label: "Privacy policy", icon: Shield },
            { key: "show_top_terms", label: "Terms of service", icon: Scale },
        ],
    },
    {
        title: "User dropdown",
        subtitle:
            "Items in the avatar dropdown at the top-right. Admin is hidden here — it's already gated to superadmins only.",
        icon: UserCircle,
        rows: [
            { key: "show_dropdown_account", label: "Account", icon: UserCircle },
            { key: "show_dropdown_workgroup", label: "Workgroup", icon: Users },
            { key: "show_dropdown_hour_log", label: "Hour log", icon: Clock },
        ],
    },
    {
        title: "Header widgets",
        subtitle: "Extra elements sitting in the top-right of every page.",
        icon: Bell,
        rows: [
            { key: "show_notifications", label: "Notifications bell", icon: Bell },
        ],
    },
];

export default function DisplayTab() {
    const [flags, setFlags] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState("");

    useEffect(() => {
        let dead = false;
        (async () => {
            setLoading(true);
            try {
                const f = await adminGetFeatureFlags();
                if (!dead) setFlags(f || {});
            } catch (err) {
                toast.error(err?.response?.data?.detail || "Couldn't load display flags.");
            } finally {
                if (!dead) setLoading(false);
            }
        })();
        return () => {
            dead = true;
        };
    }, []);

    const setFlag = async (key, value) => {
        setSaving(key);
        setFlags((s) => ({ ...s, [key]: value }));
        try {
            const next = await adminUpdateFeatureFlags({ [key]: value });
            setFlags(next);
            resetFeatureFlagsCache();
            toast.success(`${key.replace(/^show_/, "").replace(/_/g, " ")} ${value ? "shown" : "hidden"}`);
        } catch (err) {
            setFlags((s) => ({ ...s, [key]: !value }));
            toast.error(err?.response?.data?.detail || "Couldn't save.");
        } finally {
            setSaving("");
        }
    };

    return (
        <div className="space-y-5" data-testid="admin-display">
            <section className="bg-[#131A2F]/80 border border-[#2A3655] rounded-md overflow-hidden">
                <header className="flex items-center justify-between px-5 py-3 border-b border-[#2A3655] bg-[#1C253C]">
                    <h2 className="text-base font-bold text-white tracking-tight flex items-center gap-2">
                        <Eye className="w-4 h-4 text-[#00A3FF]" />
                        Display
                    </h2>
                    <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                        Persist through updates
                    </span>
                </header>
                <div className="p-5 space-y-6">
                    <p className="text-xs text-slate-400">
                        Toggle which nav items are visible across the app. Settings live in the database, so they
                        survive VPS updates and container rebuilds — no re-configuring after each deploy.{" "}
                        <span className="text-slate-500">
                            (Theme is a per-user setting — flip it on your own Account page.)
                        </span>
                    </p>

                    {loading ? (
                        <div className="text-sm text-slate-500">Loading…</div>
                    ) : (
                        GROUPS.map((group) => {
                            const HeaderIcon = group.icon;
                            return (
                                <div
                                    key={group.title}
                                    className="rounded-sm border border-[#2A3655] bg-[#0A1128]/60 overflow-hidden"
                                    data-testid={`display-group-${group.title.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    <div className="flex items-start gap-3 px-4 py-3 border-b border-[#2A3655] bg-[#0F1B3A]/60">
                                        <HeaderIcon className="w-4 h-4 mt-0.5 text-[#00A3FF] shrink-0" />
                                        <div className="min-w-0">
                                            <div className="text-sm font-bold text-white">
                                                {group.title}
                                            </div>
                                            <p className="text-[11px] text-slate-400 leading-relaxed mt-0.5">
                                                {group.subtitle}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        {group.rows.map((row) => {
                                            const Icon = row.icon;
                                            const on = flags[row.key] !== false;
                                            const isBusy = saving === row.key;
                                            return (
                                                <label
                                                    key={row.key}
                                                    className="flex items-center gap-3 px-3 py-2.5 bg-[#0A1128] border border-[#2A3655] rounded-sm cursor-pointer hover:border-[#00A3FF]/60 transition-colors"
                                                    data-testid={`display-row-${row.key}`}
                                                >
                                                    <Icon className="w-4 h-4 text-[#00A3FF] shrink-0" />
                                                    <span className="flex-1 text-sm text-white min-w-0 truncate">
                                                        {row.label}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        role="switch"
                                                        aria-checked={on}
                                                        onClick={() => setFlag(row.key, !on)}
                                                        disabled={isBusy}
                                                        data-testid={`display-toggle-${row.key}`}
                                                        className={`relative inline-flex shrink-0 h-6 w-11 rounded-full transition-colors disabled:opacity-50 ${
                                                            on ? "bg-[#007AFF]" : "bg-[#2A3655]"
                                                        }`}
                                                    >
                                                        <span
                                                            className={`absolute top-0.5 inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
                                                                on ? "translate-x-5" : "translate-x-0.5"
                                                            }`}
                                                        />
                                                        {isBusy && (
                                                            <Loader2 className="w-3 h-3 absolute -right-5 top-1.5 animate-spin text-slate-400" />
                                                        )}
                                                    </button>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </section>
        </div>
    );
}
